import React, { useEffect, useState } from "react";
import { Card, Row, Col, Upload, Space, Button, Divider, message } from "antd";
import { DownloadOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import CustomTable from "../../components/Common/CustomTable/CustomTable";
import SearchBar from "../../components/Common/SearchBar/SearchBar";

const { Dragger } = Upload;

const MasterUploadTab = ({
    title,
    rowKey,
    columns,
    requiredFields,
    searchPlaceholder,
    getList,
    uploadData,
    downloadTemplate,
    listData,
    pagination,
    gettingList,
    uploading,
    downloadingTemplate,
}) => {
    const dispatch = useDispatch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const fetchList = (p = page, l = limit, s = searchValue) => {
        dispatch(getList({ page: p, limit: l, search: s }));
    };

    useEffect(() => {
        fetchList(page, limit, searchValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, searchValue]);

    const beforeUpload = (file) => {
        const isExcel =
            file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "application/vnd.ms-excel";

        if (!isExcel) {
            message.error("Please upload an Excel file.");
            return Upload.LIST_IGNORE;
        }

        setSelectedFile(file);
        return false;
    };

    const handleUpload = () => {
        if (!selectedFile) {
            message.warning("Please select an Excel file.");
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const workbook = XLSX.read(e.target.result, { type: "binary" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const excelData = XLSX.utils
                .sheet_to_json(worksheet)
                .filter((row) =>
                    Object.values(row).some(
                        (value) => value !== undefined && value !== null && value.toString().trim() !== "",
                    ),
                );

            const validationErrors = [];

            excelData.forEach((row, index) => {
                const rowNumber = index + 2; // Excel row number (header is row 1)
                const missingFields = requiredFields.filter((field) => !row[field.key]?.toString().trim());

                if (missingFields.length) {
                    validationErrors.push(
                        `Row ${rowNumber}: ${missingFields.map((field) => field.label).join(", ")} ${
                            missingFields.length > 1 ? "are" : "is"
                        } missing`,
                    );
                }
            });

            if (validationErrors.length) {
                message.error({
                    content: (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                Upload aborted due to missing required fields:
                            </div>
                            {validationErrors.map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </div>
                    ),
                    duration: 8,
                });
                return;
            }

            dispatch(uploadData({ data: excelData })).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setSelectedFile(null);
                    setPage(1);
                    fetchList(1, limit, searchValue);
                }
            });
        };

        reader.readAsBinaryString(selectedFile);
    };

    return (
        <div className="pt-6">
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span className="text-[#1570EF] font-semibold text-base">Download Template</span>
                        }
                        className="rounded-xl shadow-sm border border-[#EAECF0] h-full"
                    >
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Dragger
                                showUploadList={false}
                                onClick={() => dispatch(downloadTemplate())}
                                openFileDialogOnClick={false}
                                className="cursor-default"
                            >
                                <p className="ant-upload-drag-icon">
                                    <DownloadOutlined />
                                </p>
                                <p className="ant-upload-text">Download the {title} template</p>
                                <p className="ant-upload-hint">Click to download.</p>
                            </Dragger>

                            <Button
                                block
                                type="primary"
                                icon={<DownloadOutlined />}
                                loading={downloadingTemplate}
                                onClick={() => dispatch(downloadTemplate())}
                            >
                                Download Template
                            </Button>
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        title={<span className="text-[#1570EF] font-semibold text-base">Upload {title}</span>}
                        className="rounded-xl shadow-sm border border-[#EAECF0] h-full"
                    >
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Dragger
                                beforeUpload={beforeUpload}
                                maxCount={1}
                                accept=".xlsx,.xls"
                                fileList={selectedFile ? [selectedFile] : []}
                                onRemove={() => setSelectedFile(null)}
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag Excel file here</p>
                                <p className="ant-upload-hint">Only .xlsx files are supported.</p>
                            </Dragger>

                            <Button
                                block
                                type="primary"
                                icon={<UploadOutlined />}
                                loading={uploading}
                                disabled={!selectedFile}
                                onClick={handleUpload}
                            >
                                Upload {title}
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Divider />

            <div className="mb-4">
                <SearchBar
                    className="!w-[330px]"
                    onSearch={(value) => {
                        setSearchValue(value);
                        setPage(1);
                    }}
                    placeholder={searchPlaceholder}
                />
            </div>

            <CustomTable
                rowKey={rowKey}
                loading={gettingList}
                columns={columns}
                dataSource={listData}
                pagination={{
                    current: pagination?.page || page,
                    pageSize: pagination?.limit || limit,
                    total: pagination?.total || 0,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    onChange: (p, l) => {
                        setPage(p);
                        setLimit(l);
                    },
                    onShowSizeChange: (p, l) => {
                        setPage(p);
                        setLimit(l);
                    },
                }}
            />
        </div>
    );
};

export default MasterUploadTab;
